"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenOberoesterreich } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Oberoesterreich() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Oberösterreich verkaufen: So geht’s</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Oberösterreich.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Oberösterreich"
        link="/auto-verkaufen/oberoesterreich"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/oberoesterreich"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Du willst dein Auto in Oberösterreich verkaufen? Warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Wenn du dein Auto in Oberösterreich verkaufen möchtest, wirst
                    du schnell merken, dass es nicht so einfach ist. Die Nachfrage
                    nach Gebrauchtwagen ist hoch, da viele Menschen im Bundesland
                    auf das Auto angewiesen sind. Beliebt sind vor allem Klein-
                    und Mittelklassewagen für Pendler zwischen Linz, Wels oder
                    Steyr. Gleichzeitig sind auch Kombis und SUVs gefragt, da sie
                    sich gut für Familien oder längere Strecken eignen.
                  </p>
                  <p>
                    Der Verkaufsprozess selbst kostet jedoch Zeit und Nerven.
                    Viele Fahrzeuge in Oberösterreich haben bereits durch
                    Pendelverkehr oder häufige Autobahnfahrten höhere
                    Kilometerstände. Dazu kommen Inserate, Preisverhandlungen und
                    das Risiko von unseriösen Angeboten.
                  </p>
                  <p>
                    Mit Autoeinfachlos verkaufst du dein Auto in Oberösterreich
                    einfach, transparent und sicher.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Oberösterreich – worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Der Markt in Oberösterreich ist vielfältig. In Städten wie
                    Linz oder Wels sind Klein- und Mittelklassewagen besonders
                    beliebt. Im ländlichen Raum setzen viele auf robuste
                    Fahrzeuge, die auch für längere Fahrten geeignet sind.
                  </p>

                  <p>
                    <strong>Typische Herausforderungen beim Auto verkaufen in Oberösterreich:</strong>
                  </p>
                  <ul className="custom-unordered-list">
                    <li>viele Anfragen und Terminvereinbarungen</li>
                    <li>Unsicherheit beim richtigen Preis</li>
                    <li>Gefahr von Zahlungsausfällen oder unseriösen Käufern</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos ersparst du dir diesen Aufwand. Wir
                    bringen dein Auto direkt zu unserem großen Händlernetzwerk.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Oberösterreich</h3>
                  <p>Mit Autoeinfachlos wird der Autoverkauf für dich stressfrei:</p>
                  <ul className="custom-unordered-list">
                    <li>
                      Zugang zu über 5.000 Händlern, viele davon auch in
                      Oberösterreich
                    </li>
                    <li>
                      Auktion statt Einzelverhandlung, so erhältst du automatisch
                      den besten Preis
                    </li>
                    <li>
                      Abholung direkt bei dir, egal ob in Linz, Wels, Steyr oder
                      im ländlichen Raum
                    </li>
                    <li>schnelle und sichere Bezahlung</li>
                  </ul>
                  <p>So verkaufst du dein Auto ohne Risiko und mit fairen Konditionen.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Oberösterreich - privat oder Händler?"
              rightSecondText={
                <>
                  <p>
                    Viele überlegen, ihr Auto privat zu verkaufen. Doch der
                    Privatverkauf in Oberösterreich hat klare Nachteile:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>hoher Zeitaufwand für Inserate und Probefahrten</li>
                    <li>viele Absagen und unseriöse Kontakte</li>
                    <li>Risiko von Preisdrückerei</li>
                  </ul>

                  <p>Der Autoankauf über unser Händlernetzwerk bietet dir entscheidende Vorteile:</p>
                  <ul className="custom-unordered-list secondary">
                    <li>geprüfte Käufer</li>
                    <li>professionelle Abwicklung</li>
                    <li>schnelle und sichere Zahlung</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos kombinierst du die Reichweite vieler
                    Händler mit der Sicherheit einer geprüften Plattform.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Oberösterreich wert?"
                alt="Auto in Oberösterreich bewerten"
                text={
                  <>
                    <p>Der Wert deines Autos hängt von vielen Faktoren ab:</p>
                    <ul className="custom-unordered-list">
                      <li>Marke und Modell</li>
                      <li>Baujahr und Kilometerstand</li>
                      <li>Zustand und eventuelle Schäden</li>
                      <li>regionale Nachfrage in Oberösterreich</li>
                    </ul>
                    <p>
                      Mit Autoeinfachlos kannst du dein Auto kostenlos bewerten
                      lassen. Du erhältst eine erste Einschätzung und startest
                      direkt in die Auktion.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Oberösterreich</h3>
                    <p>Der Ablauf ist einfach, transparent und auf Dich zugeschnitten:</p>
                    <ul className="custom-unordered-list">
                      <li>Auto online bewerten lassen – kostenlos und unverbindlich.</li>
                      <li>
                        Auktion starten – Dein Auto wird in unserem Händlernetzwerk
                        präsentiert.
                      </li>
                      <li>
                        Höchstgebot erhalten – mehrere Händler bieten, du
                        entscheidest.
                      </li>
                      <li>
                        Abholung vor Ort in Oberösterreich – Dein Auto wird direkt
                        bei dir in Oberösterreich übernommen.
                      </li>
                      <li>Sichere Bezahlung – schnell und unkompliziert.</li>
                    </ul>
                    <p>
                      Damit sparst du Zeit, verhinderst unnötigen Stress und
                      erhältst den besten Preis für dein Fahrzeug.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Autoverkauf in Oberösterreich"
              fragen={fragenOberoesterreich}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Oberösterreich "
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Oberösterreich mit Autoeinfachlos"
                alt="Auto verkaufen in Oberösterreich mit Autoeinfachlos"
                text={
                  <>
                    <p>
                      Wenn du dein Auto in Oberösterreich verkaufen möchtest,
                      musst du dich nicht mit Inseraten, unsicheren Käufern oder
                      langen Preisverhandlungen belasten. Mit Autoeinfachlos nutzt
                      du ein starkes Händlernetzwerk, regionale Abholung und eine
                      sichere Bezahlung.
                    </p>
                    <p>Starte jetzt mit einer kostenlosen Bewertung:</p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Oberoesterreich;
